import type { LinkProps } from '@tanstack/react-router'

import CardState, { type CardStateValue } from '@/components/ui/CardState'
import EmptyState from '@/components/ui/EmptyState'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import { useBrokerHighlights } from '@/features/broker-rail/api/queries'
import {
  BROKER_HIGHLIGHTS_AVAILABLE,
  BROKER_HIGHLIGHTS_LIMIT,
  type BrokerHighlight,
} from '@/features/broker-rail/api/highlightsService'
import RailNewsRow, { RailNewsRowSkeleton } from '@/features/market-rail/components/RailNewsRow'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { formatBsDate } from '@/lib/bsDate'

const SKELETON_ROWS = Array.from({ length: BROKER_HIGHLIGHTS_LIMIT }, (_, index) => (
  <RailNewsRowSkeleton key={index} />
))

/** Where a note opens: the house it is about, or the list it summarises. */
const linkOf = (item: BrokerHighlight): LinkProps =>
  item.target === 'broker' && item.brokerCode !== null
    ? { to: '/brokers/$code', params: { code: item.brokerCode } }
    : item.target === 'most-traded'
      ? { to: '/brokers/most-traded' }
      : item.target === 'sector-flow'
        ? { to: '/brokers/sector-flow' }
        : { to: '/brokers/large-trades' }

/** "Floorsheet, 2 Bhadra 2083, about Broker 58", each part left out when the note lacks it. */
const metaOf = (item: BrokerHighlight): Array<string | undefined> => [
  item.source,
  formatBsDate(item.publishedOn),
  item.brokerCode === null ? undefined : `about Broker ${item.brokerCode}`,
]

/**
 * The Broker rail's first block (the mock's `bra-rail__block`): four of today's broker notes — the
 * single largest trade, how many houses traded, a house's buying streak — each an eyebrow over a
 * headline, as Market news draws them (`RailNewsRow`), opening the house or list it is about.
 * "More" opens Large trades, as the mock's does.
 *
 * No data yet, by owner ruling: `BROKER_HIGHLIGHTS_AVAILABLE` is false, so the head draws over the
 * generic "Not available yet" state and no request is made (contract: asks/broker-rail.md).
 */
export default function TodayHighlightsCard() {
  const block = useRailBlock('highlights')
  const highlights = useBrokerHighlights()
  const { data, isPending, isError } = highlights
  const manual = useManualRefresh([highlights])

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data.length === 0
        ? 'empty'
        : manual.isManual
          ? 'refreshing'
          : 'loaded'

  return (
    <section aria-label="Today’s highlights" {...block}>
      <SectionHead
        size="sm"
        title="Today’s highlights"
        action={
          <SeeAllLink to="/brokers/large-trades" size="rail" aria-label="More broker highlights">
            More
          </SeeAllLink>
        }
      />
      <div className="mt-2">
        {BROKER_HIGHLIGHTS_AVAILABLE ? (
          <CardState
            state={state}
            skeleton={<RailRows block="highlights" rows={SKELETON_ROWS} />}
            onRetry={manual.refresh}
            failedTitle="Couldn't load the highlights"
            failedDescription="They will be here when the broker feed answers."
            emptyTitle="Nothing to note yet"
            emptyDescription="Notes on the day's broker activity show here once trading starts."
          >
            {data === undefined ? null : (
              <RailRows
                block="highlights"
                rows={data.slice(0, BROKER_HIGHLIGHTS_LIMIT).map((item) => (
                  <RailNewsRow
                    key={item.id}
                    meta={metaOf(item)}
                    title={item.title}
                    link={linkOf(item)}
                  />
                ))}
              />
            )}
          </CardState>
        ) : (
          <EmptyState kind="not-available" thing="Today’s highlights" size="inline" />
        )}
      </div>
    </section>
  )
}
